import { useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import { useTestCases, type TestCasesController } from "../../../controllers/useTestCases";
import type {
  CreateTestCaseRequest,
  TestCase,
  TestCasePriority,
  TestCaseStatus,
  UpdateTestCaseRequest,
} from "../../../models/eqms";
import { TEST_CASE_PRIORITY_LABEL, TEST_CASE_STATUS_LABEL, formatDateTime } from "../../../models/eqms";

const PRIORITIES: TestCasePriority[] = ["LOW", "NORMAL", "HIGH", "CRITICAL"];
const STATUSES: TestCaseStatus[] = ["DRAFT", "ACTIVE", "DEPRECATED"];

type Sub =
  | { view: "list" }
  | { view: "detail"; id: string }
  | { view: "form"; id: string | null };

/** Editable local step (no id/order — the backend renumbers on save). */
interface StepDraft {
  stepProcedure: string;
  expectedResult: string;
}

/**
 * 사이버보안/SW/AI 업무유형의 "테스트" 레인: 관리 대상 테스트케이스의 레지스트리(목록)·상세·폼(순서있는 스텝
 * 편집). 데이터 오케스트레이션은 useTestCases 훅이 소유하고, 이 뷰는 상태 렌더 + 핸들러 호출만 한다.
 */
export function EqmsTestCaseView({ eqms }: { eqms: EqmsController }) {
  const tc = useTestCases(eqms.getToken, eqms.projectId, eqms.workArea);
  const [sub, setSub] = useState<Sub>({ view: "list" });

  if (!eqms.projectId) return <div className="note">프로젝트를 먼저 선택하세요.</div>;
  if (!eqms.workArea) return <div className="note">업무유형을 먼저 선택하세요.</div>;

  return (
    <div>
      {tc.error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {tc.error}
        </div>
      )}

      {sub.view === "list" && (
        <TestCaseList tc={tc} onOpen={(id) => setSub({ view: "detail", id })} onNew={() => setSub({ view: "form", id: null })} />
      )}
      {sub.view === "detail" && (
        <TestCaseDetail
          tc={tc}
          id={sub.id}
          onBack={() => setSub({ view: "list" })}
          onEdit={(id) => setSub({ view: "form", id })}
        />
      )}
      {sub.view === "form" && (
        <TestCaseForm
          tc={tc}
          workArea={eqms.workArea}
          id={sub.id}
          onDone={(id) => setSub({ view: "detail", id })}
          onCancel={() => setSub(sub.id ? { view: "detail", id: sub.id } : { view: "list" })}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: TestCaseStatus }) {
  const tint: Record<TestCaseStatus, string> = { DRAFT: "#eef2f7", ACTIVE: "#eafaf0", DEPRECATED: "#f3f3f3" };
  const color: Record<TestCaseStatus, string> = { DRAFT: "var(--ink3)", ACTIVE: "#2e7d32", DEPRECATED: "var(--ink3)" };
  return <span className="tag" style={{ background: tint[status], color: color[status] }}>{TEST_CASE_STATUS_LABEL[status]}</span>;
}

function PriorityBadge({ priority }: { priority: TestCasePriority }) {
  const color: Record<TestCasePriority, string> = {
    LOW: "var(--ink3)", NORMAL: "var(--ink2)", HIGH: "#c77700", CRITICAL: "#c62828",
  };
  return <span className="mono" style={{ color: color[priority], fontSize: 13 }}>{TEST_CASE_PRIORITY_LABEL[priority]}</span>;
}

// ---- List (registry) ----
function TestCaseList({ tc, onOpen, onNew }: { tc: TestCasesController; onOpen: (id: string) => void; onNew: () => void }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            className="sdui-input"
            placeholder="🔎 코드·제목·설명 검색"
            value={tc.filters.q}
            onChange={(e) => tc.setFilters({ ...tc.filters, q: e.target.value })}
            style={{ width: 220 }}
          />
          <select
            className="sdui-input"
            value={tc.filters.status}
            onChange={(e) => tc.setFilters({ ...tc.filters, status: e.target.value as TestCaseStatus | "" })}
            style={{ width: 130 }}
          >
            <option value="">상태 전체</option>
            {STATUSES.map((s) => <option key={s} value={s}>{TEST_CASE_STATUS_LABEL[s]}</option>)}
          </select>
          {(tc.filters.q || tc.filters.status) && (
            <button className="btn" style={{ width: "auto" }} onClick={() => tc.setFilters({ status: "", q: "" })}>초기화</button>
          )}
          <span className="note" style={{ margin: 0 }}>{tc.cases.length}건</span>
        </div>
        <button className="btn primary" style={{ width: "auto" }} onClick={onNew}>＋ 테스트케이스</button>
      </div>

      {tc.loading ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="sdui-table">
            <thead>
              <tr>
                <th>코드</th><th>제목</th><th>우선순위</th><th>상태</th><th>태그</th><th style={{ textAlign: "center" }}>스텝</th><th>수정일</th>
              </tr>
            </thead>
            <tbody>
              {tc.cases.map((c) => (
                <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => onOpen(c.id)}>
                  <td className="mono">{c.code}</td>
                  <td>{c.title}</td>
                  <td><PriorityBadge priority={c.priority} /></td>
                  <td><StatusBadge status={c.status} /></td>
                  <td>{c.tags.map((t) => <span key={t} className="tag" style={{ marginRight: 4 }}>{t}</span>)}</td>
                  <td style={{ textAlign: "center" }}>{c.steps.length}</td>
                  <td className="mono" style={{ fontSize: 13 }}>{formatDateTime(c.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
            {tc.cases.length === 0 && (
              <tfoot><tr><td colSpan={7} className="sdui-table-empty">테스트케이스가 없습니다. ＋로 추가하세요.</td></tr></tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Detail ----
function TestCaseDetail({ tc, id, onBack, onEdit }: {
  tc: TestCasesController; id: string; onBack: () => void; onEdit: (id: string) => void;
}) {
  const [item, setItem] = useState<TestCase | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    tc.load(id).then((c) => { if (alive) { setItem(c); setLoading(false); } }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function del() {
    if (!window.confirm("이 테스트케이스와 절차·추적성 링크가 영구 삭제됩니다. 되돌릴 수 없습니다. 계속할까요?")) return;
    if (await tc.remove(id)) onBack();
  }

  if (loading) return <div className="center">불러오는 중…</div>;
  if (!item) return <div className="sdui-list-empty">테스트케이스를 찾을 수 없습니다. <button className="btn" style={{ width: "auto" }} onClick={onBack}>목록</button></div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button className="btn" style={{ width: "auto" }} onClick={onBack}>← 테스트케이스</button>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" style={{ width: "auto" }} onClick={() => onEdit(id)}>편집</button>
          <button className="btn" style={{ width: "auto", color: "#c62828" }} disabled={tc.busy} onClick={del}>삭제</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 2, minWidth: 320 }}>
          <div className="info-card" style={{ marginBottom: 16 }}>
            <div className="mono" style={{ fontSize: 13, color: "var(--ink3)" }}>{item.code}</div>
            <h3 style={{ margin: "4px 0 8px" }}>{item.title}</h3>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <PriorityBadge priority={item.priority} /> <StatusBadge status={item.status} />
              {item.tags.map((t) => <span key={t} className="tag">{t}</span>)}
            </div>
            {item.description && <p style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{item.description}</p>}
            {item.testCriteria && (
              <>
                <h5 style={{ margin: "10px 0 4px" }}>시험 기준</h5>
                <p style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{item.testCriteria}</p>
              </>
            )}
          </div>

          <div className="info-card">
            <h4>절차 · {item.steps.length}</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {item.steps.map((s, i) => (
                <div key={s.id} className="screen-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
                  <div style={{ display: "flex", gap: 12 }}>
                    <span className="tag" style={{ height: "fit-content" }}>Step {i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14 }}><b>절차</b> {s.stepProcedure}</div>
                      <div style={{ fontSize: 14, color: "var(--ink2)" }}><b>기대결과</b> {s.expectedResult}</div>
                    </div>
                  </div>
                </div>
              ))}
              {item.steps.length === 0 && <div className="sdui-list-empty">절차가 없습니다.</div>}
            </div>
          </div>
        </div>

        <aside style={{ flex: 1, minWidth: 220 }}>
          <div className="info-card">
            <h4>요약</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span className="note" style={{ margin: 0 }}>우선순위</span><PriorityBadge priority={item.priority} /></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span className="note" style={{ margin: 0 }}>상태</span><StatusBadge status={item.status} /></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span className="note" style={{ margin: 0 }}>스텝</span><span>{item.steps.length}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span className="note" style={{ margin: 0 }}>수정</span><span className="mono" style={{ fontSize: 13 }}>{formatDateTime(item.updatedAt)}</span></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---- Form (create / edit + step editor) ----
function TestCaseForm({ tc, workArea, id, onDone, onCancel }: {
  tc: TestCasesController;
  workArea: TestCase["workAreaType"];
  id: string | null;
  onDone: (id: string) => void;
  onCancel: () => void;
}) {
  const isEdit = id != null;
  const [loaded, setLoaded] = useState(!isEdit);
  const [existing, setExisting] = useState<TestCase | null>(null);
  const [code, setCode] = useState("");
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<TestCasePriority>("NORMAL");
  const [description, setDescription] = useState("");
  const [testCriteria, setTestCriteria] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [steps, setSteps] = useState<StepDraft[]>([{ stepProcedure: "", expectedResult: "" }]);

  // Load the existing case once when editing.
  useEffect(() => {
    if (!isEdit || !id) return;
    let alive = true;
    tc.load(id).then((c) => {
      if (!alive) return;
      setExisting(c);
      setCode(c.code);
      setTitle(c.title);
      setPriority(c.priority);
      setDescription(c.description ?? "");
      setTestCriteria(c.testCriteria ?? "");
      setTagsText(c.tags.join(", "));
      setSteps(c.steps.length ? c.steps.map((s) => ({ stepProcedure: s.stepProcedure ?? "", expectedResult: s.expectedResult ?? "" })) : [{ stepProcedure: "", expectedResult: "" }]);
      setLoaded(true);
    }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function setStep(i: number, patch: Partial<StepDraft>) {
    setSteps((cur) => cur.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }
  function moveStep(i: number, dir: -1 | 1) {
    setSteps((cur) => {
      const j = i + dir;
      if (j < 0 || j >= cur.length) return cur;
      const next = [...cur];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }
  const parsedTags = tagsText.split(",").map((t) => t.trim()).filter(Boolean);
  const cleanSteps = steps.filter((s) => s.stepProcedure.trim() || s.expectedResult.trim());

  async function submit() {
    if (!code.trim() || !title.trim()) return;
    let targetId = id;
    if (isEdit && id) {
      const req: UpdateTestCaseRequest = {
        title: title.trim(), description, testCriteria, priority, tags: parsedTags,
      };
      const updated = await tc.update(id, req);
      if (!updated) return;
      targetId = updated.id;
    } else {
      const req: CreateTestCaseRequest = {
        workAreaType: workArea, code: code.trim(), title: title.trim(),
        description: description || undefined, testCriteria: testCriteria || undefined,
        priority, tags: parsedTags,
      };
      const created = await tc.create(req);
      if (!created) return;
      targetId = created.id;
    }
    if (targetId) {
      const saved = await tc.saveSteps(targetId, { steps: cleanSteps.map((s) => ({ stepProcedure: s.stepProcedure, expectedResult: s.expectedResult })) });
      if (saved) onDone(saved.id);
    }
  }

  if (!loaded) return <div className="center">불러오는 중…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button className="btn" style={{ width: "auto" }} onClick={onCancel}>← 취소</button>
        <h3 style={{ margin: 0 }}>{isEdit ? `테스트케이스 편집 · ${existing?.code ?? ""}` : "새 테스트케이스"}</h3>
      </div>

      <div className="info-card" style={{ marginBottom: 16 }}>
        <h4>기본 정보</h4>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <label className="sdui-field" style={{ minWidth: 160 }}>
            <span className="sdui-label">코드 *</span>
            <input className="sdui-input mono" value={code} disabled={isEdit} placeholder="TC-AUTH-001"
              onChange={(e) => setCode(e.target.value)} />
          </label>
          <label className="sdui-field" style={{ flex: 2, minWidth: 240 }}>
            <span className="sdui-label">제목 *</span>
            <input className="sdui-input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="sdui-field" style={{ minWidth: 120 }}>
            <span className="sdui-label">우선순위</span>
            <select className="sdui-input" value={priority} onChange={(e) => setPriority(e.target.value as TestCasePriority)}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{TEST_CASE_PRIORITY_LABEL[p]}</option>)}
            </select>
          </label>
        </div>
        <label className="sdui-field" style={{ marginTop: 10 }}>
          <span className="sdui-label">태그 (쉼표 구분)</span>
          <input className="sdui-input" value={tagsText} placeholder="owasp, smoke" onChange={(e) => setTagsText(e.target.value)} />
        </label>
        <label className="sdui-field" style={{ marginTop: 10 }}>
          <span className="sdui-label">설명</span>
          <textarea className="sdui-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <label className="sdui-field" style={{ marginTop: 10 }}>
          <span className="sdui-label">시험 기준</span>
          <textarea className="sdui-input" rows={2} value={testCriteria} onChange={(e) => setTestCriteria(e.target.value)} />
        </label>
      </div>

      <div className="info-card" style={{ marginBottom: 16 }}>
        <h4>절차 (Steps)</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {steps.map((s, i) => (
            <div key={i} className="screen-row" style={{ alignItems: "flex-start", gap: 8 }}>
              <span className="tag" style={{ marginTop: 6 }}>{i + 1}</span>
              <div style={{ flex: 1, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <textarea className="sdui-input" rows={2} placeholder="절차" value={s.stepProcedure}
                  onChange={(e) => setStep(i, { stepProcedure: e.target.value })} style={{ flex: 1, minWidth: 180 }} />
                <textarea className="sdui-input" rows={2} placeholder="기대결과" value={s.expectedResult}
                  onChange={(e) => setStep(i, { expectedResult: e.target.value })} style={{ flex: 1, minWidth: 180 }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <button className="btn" style={{ width: "auto", padding: "2px 8px" }} disabled={i === 0} onClick={() => moveStep(i, -1)}>↑</button>
                <button className="btn" style={{ width: "auto", padding: "2px 8px" }} disabled={i === steps.length - 1} onClick={() => moveStep(i, 1)}>↓</button>
                <button className="btn" style={{ width: "auto", padding: "2px 8px" }} disabled={steps.length === 1} onClick={() => setSteps((cur) => cur.filter((_, idx) => idx !== i))}>×</button>
              </div>
            </div>
          ))}
        </div>
        <button className="btn" style={{ width: "auto", marginTop: 10 }} onClick={() => setSteps((cur) => [...cur, { stepProcedure: "", expectedResult: "" }])}>＋ 스텝 추가</button>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button className="btn" style={{ width: "auto" }} onClick={onCancel}>취소</button>
        <button className="btn primary" style={{ width: "auto" }} disabled={tc.busy || !code.trim() || !title.trim()} onClick={submit}>
          {tc.busy ? "저장 중…" : "저장"}
        </button>
      </div>
    </div>
  );
}
